/**
 * Cart — multi-store + restaurant cart page with conversion optimizations.
 *
 * Major upgrades over the previous version:
 *   • Shows product thumbnails alongside name + qty stepper
 *   • Free-delivery progress bar per store (drives upsell)
 *   • Quantity steppers use the design-system QuantityStepper
 *   • Empty state uses the EmptyState primitive
 *   • Continue-shopping CTA goes to the relevant vertical
 *
 * Behavioural compatibility:
 *   - ?store_id=N still scopes to a single store cart (used by /store/:id)
 *   - Without it, every store-cart that has items renders as its own card.
 *   - Restaurant cart is still client-side (useRestaurantCart) and surfaces
 *     here too.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { Trash2, ShoppingBag, ArrowLeft, Store as StoreIcon } from 'lucide-react';
import { cartAPI, Cart, CartSummary } from '../api/cart';
import { productsAPI, Product } from '../api/products';
import { useRestaurantCart } from '../stores/restaurantCart';
import { useAuthStore } from '../stores/authStore';
import { Button, Card, Badge, EmptyState, css } from '../design';
import QuantityStepper from '../components/QuantityStepper';
import SmartImage from '../components/SmartImage';
import FreeDeliveryProgress from '../components/cart/FreeDeliveryProgress';
import PageHead from '../components/seo/PageHead';

const FREE_DELIVERY_THRESHOLD = 299;

export function CartPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const restaurantCart = useRestaurantCart();
  const [summary, setSummary] = useState<CartSummary | null>(null);
  const [carts, setCarts] = useState<Map<number, Cart>>(new Map());
  const [products, setProducts] = useState<Map<number, Product>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // store_id=N → single-store mode, otherwise multi-store mode.
  const urlParams = new URLSearchParams(window.location.search);
  const explicitStoreId = urlParams.get('store_id') ? Number(urlParams.get('store_id')) : null;

  const loadCart = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      if (!user) { navigate('/login'); return; }

      let storeIds: number[];
      if (explicitStoreId !== null) {
        storeIds = [explicitStoreId];
        setSummary(null);
      } else {
        const s = await cartAPI.getSummary();
        setSummary(s);
        storeIds = (s.stores || []).map((x) => x.store_id);
      }

      const loaded = new Map<number, Cart>();
      await Promise.all(
        storeIds.map(async (sid) => {
          try {
            const c = await cartAPI.getCart(sid);
            if ((c.items || []).length > 0) loaded.set(sid, c);
          } catch (e) {
            console.warn(`[Cart] failed to load store ${sid}:`, e);
          }
        }),
      );
      setCarts(loaded);

      const productMap = new Map<number, Product>();
      const productIds = new Set<number>();
      for (const c of Array.from(loaded.values())) {
        for (const it of c.items) productIds.add(it.product_id);
      }
      await Promise.all(
        Array.from(productIds).map(async (pid) => {
          try { productMap.set(pid, await productsAPI.getProduct(pid)); }
          catch { /* fall back to "Product #<id>" */ }
        }),
      );
      setProducts(productMap);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load cart');
    } finally {
      setLoading(false);
    }
  }, [user, navigate, explicitStoreId]);

  useEffect(() => {
    loadCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, explicitStoreId]);

  const handleUpdateQuantity = async (cartItemId: number, newQuantity: number) => {
    try {
      if (newQuantity <= 0) { await handleRemoveItem(cartItemId); return; }
      await cartAPI.updateCartItem(cartItemId, newQuantity);
      await loadCart();
    } catch { setError('Failed to update quantity'); }
  };

  const handleRemoveItem = async (cartItemId: number) => {
    try { await cartAPI.removeFromCart(cartItemId); await loadCart(); }
    catch { setError('Failed to remove item'); }
  };

  const handleClearStoreCart = async (storeId: number) => {
    if (!window.confirm('Clear all items from this store?')) return;
    try { await cartAPI.clearCart(storeId); await loadCart(); }
    catch { setError('Failed to clear cart'); }
  };

  const handleCheckout = (storeId: number) => navigate(`/checkout?store_id=${storeId}`);

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" className="text-success" />
        <p className="mt-3" style={{ color: css.color.textMuted }}>Loading cart...</p>
      </Container>
    );
  }

  const groceryStoreIds = Array.from(carts.keys());
  const hasRestaurantCart =
    restaurantCart.restaurantId !== null && restaurantCart.lines.length > 0;
  const isEmpty = groceryStoreIds.length === 0 && !hasRestaurantCart;

  if (isEmpty) {
    return (
      <Container style={{ maxWidth: 720, paddingTop: 16, paddingBottom: 80 }}>
        <h2 style={{ fontFamily: css.font.display, fontWeight: 800, fontSize: 24, marginBottom: 16 }}>
          <ShoppingBag size={24} style={{ marginRight: 8, verticalAlign: -4 }} />
          Your cart
        </h2>
        {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
        <Card padding="lg">
          <EmptyState
            icon="🛒"
            title="Your cart is empty"
            description="Discover fresh groceries from your neighbourhood stores or try a new restaurant."
            action={{ label: 'Browse stores', onClick: () => navigate('/stores') }}
            secondaryAction={{ label: 'Order food', onClick: () => navigate('/restaurants') }}
          />
        </Card>
      </Container>
    );
  }

  return (
    <Container style={{ maxWidth: 960, paddingTop: 16, paddingBottom: 100 }}>
      <PageHead title="Your cart — ProvisionMart" noIndex />
      <h2 style={{ fontFamily: css.font.display, fontWeight: 800, fontSize: 24, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
        <ShoppingBag size={24} /> Your cart
      </h2>
      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      {/* Restaurant cart */}
      {hasRestaurantCart && (
        <Card padding="none" style={{ marginBottom: 16, overflow: 'hidden' }} accent="food">
          <Card.Header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Badge variant="food" size="md">FOOD</Badge>
              <strong style={{ color: css.color.text }}>{restaurantCart.restaurantName}</strong>
            </div>
            <small style={{ color: css.color.textMuted }}>{restaurantCart.itemCount()} items</small>
          </Card.Header>
          <Card.Body>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {restaurantCart.lines.map((line) => (
                <div key={line.menu_item_id} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, color: css.color.text }}>{line.name}</div>
                    <small style={{ color: css.color.textMuted }}>₹{line.price.toFixed(2)} each</small>
                  </div>
                  <div style={{ width: 110, flexShrink: 0 }}>
                    <QuantityStepper
                      value={line.quantity}
                      onChange={(next) => {
                        if (next <= 0) restaurantCart.remove(line.menu_item_id);
                        else restaurantCart.setQuantity(line.menu_item_id, next);
                      }}
                      min={0}
                      max={99}
                    />
                  </div>
                  <div style={{ minWidth: 80, textAlign: 'right', fontWeight: 800, color: css.color.text }}>
                    ₹{(line.price * line.quantity).toFixed(0)}
                  </div>
                </div>
              ))}
            </div>
          </Card.Body>
          <Card.Footer style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Button variant="ghost" size="sm" onClick={() => restaurantCart.clear()} leadingIcon={<Trash2 size={14} />}>
              Clear cart
            </Button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ fontFamily: css.font.display, fontWeight: 800, fontSize: 18, color: css.color.food }}>
                Total: ₹{restaurantCart.total().toFixed(0)}
              </div>
              <Button variant="accent" size="lg" onClick={() => navigate('/restaurant-checkout')}>
                Proceed to checkout
              </Button>
            </div>
          </Card.Footer>
        </Card>
      )}

      {/* Grocery carts grouped by store */}
      <Row className="g-3">
        {groceryStoreIds.map((storeId) => {
          const cart = carts.get(storeId)!;
          const subtotal = cart.items.reduce((s, it) => s + it.price_at_add * it.quantity, 0);
          const itemCount = cart.items.reduce((s, it) => s + it.quantity, 0);
          const storeName =
            summary?.stores?.find((x) => x.store_id === storeId)?.store_name ||
            `Store #${storeId}`;
          return (
            <Col xs={12} key={storeId}>
              <Card padding="none" accent="primary" style={{ overflow: 'hidden' }}>
                <Card.Header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <StoreIcon size={16} color={css.color.primary} />
                    <strong style={{ color: css.color.text }}>{storeName}</strong>
                  </div>
                  <small style={{ color: css.color.textMuted }}>
                    {itemCount} item{itemCount === 1 ? '' : 's'}
                  </small>
                </Card.Header>

                <Card.Body>
                  <FreeDeliveryProgress
                    subtotal={subtotal}
                    threshold={FREE_DELIVERY_THRESHOLD}
                    onShopMore={() => navigate(`/store/${storeId}`)}
                  />

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {cart.items.map((item) => {
                      const product = products.get(item.product_id);
                      return (
                        <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          {/* Thumb */}
                          <div
                            style={{ width: 60, height: 60, borderRadius: 8, overflow: 'hidden', flexShrink: 0, background: css.color.surface2, cursor: 'pointer' }}
                            onClick={() => product && navigate(`/product/${product.id}`)}
                          >
                            <SmartImage src={product?.image_url} thumb={product?.thumbnail_url} alt={product?.name || ''} radius={8} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{ fontWeight: 700, color: css.color.text, cursor: 'pointer' }}
                              onClick={() => product && navigate(`/product/${product.id}`)}
                            >
                              {product?.name || `Product #${item.product_id}`}
                            </div>
                            <small style={{ color: css.color.textMuted }}>
                              ₹{item.price_at_add.toFixed(0)} each
                            </small>
                            {product?.category && (
                              <div style={{ marginTop: 4 }}>
                                <Badge variant="neutral" size="sm">{product.category}</Badge>
                              </div>
                            )}
                          </div>
                          <div style={{ width: 110, flexShrink: 0 }}>
                            <QuantityStepper
                              value={item.quantity}
                              onChange={(next) => handleUpdateQuantity(item.id, next)}
                              min={0}
                              max={Math.max(1, product?.stock_quantity ?? 99)}
                            />
                          </div>
                          <div style={{ minWidth: 80, textAlign: 'right', fontWeight: 800, color: css.color.text, fontVariantNumeric: 'tabular-nums' }}>
                            ₹{(item.price_at_add * item.quantity).toFixed(0)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Card.Body>

                <Card.Footer style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <Button variant="ghost" size="sm" onClick={() => handleClearStoreCart(storeId)} leadingIcon={<Trash2 size={14} />}>
                    Clear cart
                  </Button>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <div>
                      <div style={{ fontSize: 11, color: css.color.textMuted, textAlign: 'right' }}>Subtotal</div>
                      <div style={{ fontFamily: css.font.display, fontWeight: 800, fontSize: 18, color: css.color.text }}>
                        ₹{subtotal.toFixed(0)}
                      </div>
                    </div>
                    <Button variant="primary" size="lg" onClick={() => handleCheckout(storeId)}>
                      Checkout
                    </Button>
                  </div>
                </Card.Footer>
              </Card>
            </Col>
          );
        })}
      </Row>

      <div style={{ marginTop: 18 }}>
        <Button variant="ghost" leadingIcon={<ArrowLeft size={16} />} onClick={() => navigate('/stores')}>
          Continue shopping
        </Button>
      </div>
    </Container>
  );
}

export default CartPage;

